import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import CalendarWidget from '@/components/CalendarWidget';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

function Dashboard({ user }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, notificationsRes] = await Promise.all([
          api.get('/projects'),
          api.get('/notifications'),
        ]);
        setProjects(projectsRes.data);
        setNotifications(notificationsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': 'bg-gray-200 text-gray-700',
      'Não Iniciado': 'bg-gray-200 text-gray-700',
      'In Progress': 'bg-blue-100 text-blue-700',
      'Em Andamento': 'bg-blue-100 text-blue-700',
      'Iniciado': 'bg-blue-100 text-blue-700',
      'On Hold': 'bg-yellow-100 text-yellow-700',
      'Em Espera': 'bg-yellow-100 text-yellow-700',
      'On Hold - Agency': 'bg-orange-100 text-orange-700',
      'Completed': 'bg-green-100 text-green-700',
      'Concluído': 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-gray-200 text-gray-700';
  };

  const activeProjects = projects.filter(p => ['In Progress', 'Em Andamento', 'Iniciado', 'Not Started', 'Não Iniciado'].includes(p.status));
  const completedProjects = projects.filter(p => p.status === 'Completed' || p.status === 'Concluído');
  const avgCompletion = projects.length > 0
    ? projects.reduce((sum, p) => sum + p.overall_percentage, 0) / projects.length
    : 0;

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
                        {['ADMIN', 'CONSULTANT'].includes(user?.role?.toUpperCase()) ? 'Visão geral dos projetos' : 'Acompanhe seus projetos'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 border border-gray-200" data-testid="total-projects-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Projetos</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{projects.length}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-gray-200" data-testid="active-projects-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Projetos Ativos</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{activeProjects.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-6 border border-gray-200" data-testid="completion-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conclusão Média</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">{avgCompletion.toFixed(1)}%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4" data-testid="recent-projects-title">Projetos Recentes</h2>
            <div className="space-y-4">
              {projects.slice(0, 5).map((project) => (
                <div
                  key={project.project_id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => navigate(`/projects/${project.project_id}`)}
                  data-testid={`project-item-${project.project_id}`}
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{project.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{project.project_type}</p>
                    <div className="mt-2">
                      <Progress value={project.overall_percentage} className="h-2" />
                      <p className="text-xs text-gray-500 mt-1">{project.overall_percentage.toFixed(1)}% concluído</p>
                    </div>
                  </div>
                  <Badge className={`ml-4 ${getStatusColor(project.status)}`}>
                    {project.status}
                  </Badge>
                </div>
              ))}
              {projects.length === 0 && (
                <p className="text-gray-500 text-center py-8">Nenhum projeto encontrado</p>
              )}
            </div>
          </Card>

          <CalendarWidget />
        </div>

        <Card className="p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4" data-testid="notifications-title">Notificações Recentes</h2>
          <div className="space-y-3">
            {notifications.slice(0, 5).map((notification) => (
              <div
                key={notification.notification_id}
                className={`p-4 rounded-lg border ${
                  notification.read_status ? 'bg-white border-gray-200' : 'bg-blue-50 border-blue-200'
                }`}
                data-testid={`notification-${notification.notification_id}`}
              >
                <p className="text-sm text-gray-900">{notification.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(notification.created_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
            ))}
            {notifications.length === 0 && (
              <p className="text-gray-500 text-center py-8">Nenhuma notificação</p>
            )}
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default Dashboard;